
import Dialog from 'primevue/dialog';
import {
  computed,
  defineComponent,
  onBeforeMount,
  PropType,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';
import usePixieEditor from '@/composables/usePixieEditor';
import { uploadFileToMedia } from '@/composables/configs/configuration';
import { UserState } from '@/composables/User';
import type { RcsVisualImagePreset } from '@/utils/rcs-visual-image';
import {
  blobToDataUrl,
  normalizeUploadedMediaUrl,
  processRcsVisualImageForUpload,
  RCS_VISUAL_IMAGE_PRESETS,
} from '@/utils/rcs-visual-image';

export default defineComponent({
  name: 'RcsVisualImageCropModal',

  components: {
    Dialog,
  },

  props: {
    displayDialog: {
      type: Boolean,
      required: true,
    },

    imageUrl: {
      type: String,
      required: true,
    },

    preset: {
      type: String as PropType<RcsVisualImagePreset>,
      required: true,
    },
  },

  emits: ['on-close-dialog', 'on-save'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const toast = useToast();
    const processing = ref(false);
    const imageUrl = ref(props.imageUrl);
    const content = ref<Document | null>(null);
    const pixieSrcDoc = computed(
      () => content.value && content.value.documentElement.outerHTML,
    );

    const { showPixieEditor, destroyPixieEditor } = usePixieEditor();

    const displayModal = computed(() => props.displayDialog);

    const spec = computed(() => RCS_VISUAL_IMAGE_PRESETS[props.preset]);

    const modalTitle = computed(() => (
      props.preset === 'logo'
        ? t('storeParameters.services.rcsServiceParameter.wizard.visual.crop.titleLogo')
        : t('storeParameters.services.rcsServiceParameter.wizard.visual.crop.titleBanner')
    ));

    const cropPresetLabel = computed(() => (
      props.preset === 'logo'
        ? t('storeParameters.services.rcsServiceParameter.wizard.visual.crop.presetLogo')
        : t('storeParameters.services.rcsServiceParameter.wizard.visual.crop.presetBanner')
    ));

    const handleClose = () => {
      destroyPixieEditor();
      emit('on-close-dialog');
    };

    const showError = (key: string) => {
      toast.add({
        severity: 'error',
        summary: t('error'),
        detail: t(`storeParameters.services.rcsServiceParameter.wizard.visual.errors.${key}`),
        life: 4000,
      });
    };

    const handleSave = async (data: string) => {
      processing.value = true;
      try {
        const blob = await processRcsVisualImageForUpload(data, props.preset);
        const dataUrl = await blobToDataUrl(blob);
        const uploadResult = await uploadFileToMedia(
          dataUrl,
          UserState.activeShop?.id ?? 0,
        );

        const publicUrl = normalizeUploadedMediaUrl(uploadResult?.data);
        if (!publicUrl) {
          showError('uploadFailed');
          return;
        }

        emit('on-save', publicUrl);
        handleClose();
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        if (message === 'file_too_large') {
          showError('fileTooLarge');
        } else {
          showError('uploadFailed');
        }
      } finally {
        processing.value = false;
      }
    };

    const iframeLoaded = () => {
      const pixieIframe = (
        document.querySelector('iframe.pixie-wrapper') as HTMLIFrameElement
      )?.contentWindow;

      if (!pixieIframe) {
        return;
      }

      showPixieEditor(pixieIframe, imageUrl.value, handleSave, {
        cropRatio: spec.value.cropRatio,
        cropPresetName: cropPresetLabel.value,
        openCropOnLoad: true,
      });
    };

    watch(
      () => props.imageUrl,
      (value) => {
        imageUrl.value = value;
      },
    );

    onBeforeMount(() => {
      const spmApiJs = document.createElement('script');
      spmApiJs.setAttribute('src', '/libs/pixiev2/dist/pixie.umd.js');
      content.value = document.implementation.createHTMLDocument();

      const baseIframeCss = document.createElement('link');
      baseIframeCss.setAttribute('rel', 'stylesheet');
      baseIframeCss.setAttribute('href', '/css/template-builder/baseIframePixie.css');
      content.value.head.appendChild(baseIframeCss);

      const pixieContainer = document.createElement('div');
      pixieContainer.setAttribute('id', 'editor-container');
      content.value.body.appendChild(pixieContainer);
      content.value.body.appendChild(spmApiJs);
    });

    return {
      t,
      displayModal,
      modalTitle,
      processing,
      handleClose,
      iframeLoaded,
      pixieSrcDoc,
    };
  },
});
